import {Component, Input, model, inject} from '@angular/core';
import { CommonModule } from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import { SharedModule } from '../../../shared/shared.module';
import { TruckTires } from '../../../shared/models/tire/TruckTires';
import {animate, state, style, transition, trigger} from "@angular/animations";
import {FormArray, FormBuilder, FormGroup} from "@angular/forms";
import { TireConf } from '../../../shared/models/tire/TireConf';
import {NoScrollDirective} from "../../../directives/nx-no-scroll.directive";
import { LINE_TYPE_TIRE_DETAIL, LINE_TYPE_TOTAL, LINE_TYPE_TRUCK } from "../../../shared/constants/TireConstants";
import {
  AddTireSpecificationModalComponent
} from "../add-tire-specification-modal/add-tire-specification-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {TireSpecResponse} from "../../../shared/models/tire/TireSpecResponse";
import {NoDataComponent} from "../../../shared/components/no-data/no-data.component";
import {TireLineForm} from "../../../shared/models/tire/TireLineForm";
import {NgxMaskDirective, provideNgxMask} from 'ngx-mask';
import {Sort} from "@angular/material/sort";

@Component({
  selector: 'app-truck-tires-table',
  standalone: true,
  imports: [CommonModule, SharedModule, TranslateModule, NoScrollDirective, NoDataComponent, NgxMaskDirective],
  templateUrl: './truck-tires-table.component.html',
  styleUrls: ['./truck-tires-table.component.scss'],
  providers: [provideNgxMask()],
  animations: [
    trigger('expandTrigger', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '60px'})),
      transition('expanded <=> collapsed', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class TruckTiresTableComponent {
  public tireSettings: FormArray<FormGroup<TireLineForm>>;
  public deletedTruckTires: {truckSpecId: number, tireSpecId: number}[] = [];
  protected filteredTireSetting: FormGroup<TireLineForm>[];
  private _truckTiresList: TruckTires[] = [];
  private selectedTruckSpecId: number | null = null;
  private selectedTireSpecId: number | null = null;
  isLoading = model<boolean>(false);
  isModificationPending = model<boolean>(false);
  isErrorTooltipSaveBtn = model<boolean>(false);
  readonly dialog = inject(MatDialog);

  private savedExpandedStates: Map<number, boolean> = new Map<number, boolean>();

  readonly digitMask = '0*';
  readonly digitPatterns = { '0': { pattern: /\d/ } };

  protected readonly LINE_TYPE_TRUCK = LINE_TYPE_TRUCK;
  protected readonly LINE_TYPE_TIRE_DETAIL = LINE_TYPE_TIRE_DETAIL
  protected readonly LINE_TYPE_TOTAL = LINE_TYPE_TOTAL;

  protected readonly COL_SEAT = 'seat';
  protected readonly COL_TRUCK_SPEC = 'truckSpec'

  @Input() set tireSpecId(filterTireSpecId: number) {
    this.selectedTireSpecId = filterTireSpecId;
    this.applyFilters()
  };

  @Input() set truckSpecId(filterTruckSpecId: number) {
    this.selectedTruckSpecId = filterTruckSpecId;
    this.applyFilters()
  };

  constructor(
    private formBuilder: FormBuilder,
  ) {
    this.tireSettings = this.formBuilder.array<FormGroup<TireLineForm>>([]);
  }

  @Input() set truckTiresList(value: TruckTires[]) {
    this._truckTiresList = value || [];
    this.buildTireSettingsFormArray();
  }

  get truckTiresList(): TruckTires[] {
    return this._truckTiresList;
  }

  /**
   * Apply filters based on selected truck specification and tire specification
   */
  applyFilters() {
    let truckSpecIds: number[] = []
    if (this.selectedTruckSpecId) {
      truckSpecIds = Array.of(this.selectedTruckSpecId)
    } else if (this.selectedTireSpecId) {
      truckSpecIds = this._truckTiresList
        .filter(truckTire => truckTire.tiresConf.some(conf => conf.tireSpecId === this.selectedTireSpecId))
        .map(truckTire => truckTire.truckSpecId);
    } else {
      truckSpecIds = this._truckTiresList.map(truckTire => truckTire.truckSpecId);
    }
    let truckSpecSet = new Set(truckSpecIds)
    this.filteredTireSetting = this.tireSettings.controls
      .filter((group: FormGroup<TireLineForm>) =>
        truckSpecSet.has(group.controls.truckSpecId.value)
        && (group.controls.type.value !== LINE_TYPE_TIRE_DETAIL
          || !this.selectedTireSpecId
          || (group.controls.type.value === LINE_TYPE_TIRE_DETAIL && group.controls.tireSpecId.value == this.selectedTireSpecId))) as FormGroup[];
  }


  /**
   * Save the expansion state of each truck specification in a map to restore it after rebuilding the form array when truckTiresList changes
   * @private
   */
  public saveExpandedStates(): void {
    this.savedExpandedStates = new Map<number, boolean>();
    for (const control of this.tireSettings.controls as FormGroup<TireLineForm>[]) {
      const truckSpecId = control.controls.truckSpecId?.value;
      if (truckSpecId == null) continue;
      const isExpanded = control.controls.expanded?.getRawValue() === true;
      if (!this.savedExpandedStates.has(truckSpecId)) {
        this.savedExpandedStates.set(truckSpecId, isExpanded);
      } else if (isExpanded) {
        this.savedExpandedStates.set(truckSpecId, true);
      }
    }
  }

  /**
   * Expand or collapse the tire details for a given truck specification
   * @param truckSpecId
   */
  toggleExpand(truckSpecId: number): void {
    // Find the truck line to get the current expansion state
    const truckLine = this.tireSettings.controls.find((control: FormGroup<TireLineForm>) =>
      control.controls.type.value === this.LINE_TYPE_TRUCK &&
      control.controls.truckSpecId.value === truckSpecId
    ) as FormGroup<TireLineForm> | undefined;

    const currentExpanded = truckLine?.controls.expanded.getRawValue() === true;
    const targetExpanded = !currentExpanded;

    // Update the expansion state for the truck line and all its related tire detail lines
    this.tireSettings.controls.forEach((control: FormGroup<TireLineForm>) => {
      if (control.controls.truckSpecId.value === truckSpecId) {
        control.controls.expanded.setValue(targetExpanded);
      }
    });

    this.savedExpandedStates.set(truckSpecId, targetExpanded);
  }

  /**
   * Construit le FormArray tireSettings à partir de truckTiresList
   */
  private buildTireSettingsFormArray(): void {
    this.tireSettings.clear();
    this._truckTiresList.forEach((truckTire) => {
      const expandedForTruck = this.savedExpandedStates.get(truckTire.truckSpecId) ?? false;
      // TRUCK Line
      this.tireSettings.push(this.formBuilder.group({
        type: [LINE_TYPE_TRUCK],
        expanded: [expandedForTruck],
        hasChildren: [truckTire.tiresConf.length > 0],
        seat: [truckTire.seat],
        tireLifeId: [null],
        truckSpecLabel: [truckTire.truckSpecLabel],
        truckSpecId: [truckTire.truckSpecId],
        tireSpecId: [null],
        tireSpecificationFull: [null],
        historicalMix: [null],
        targetedMix: [0],
        originalHours: [null],
        manualHours: [null],
        hasManualHours: [null],
        editingHours: [0],
        comment: [null],
        totalPercentage: [null],
        updated: [null],
        added: [false]
      }));
      // TIRE_DETAIL Lines
      truckTire.tiresConf.forEach((conf: TireConf) => {
        this.tireSettings.push(this.formBuilder.group({
          type: [LINE_TYPE_TIRE_DETAIL],
          expanded: [expandedForTruck],
          hasChildren: [null],
          seat: [null],
          tireLifeId: [conf.tireLifeId],
          truckSpecLabel: [null],
          truckSpecId: [truckTire.truckSpecId],
          tireSpecId: [conf.tireSpecId],
          tireSpecificationFull: [conf.tireSpecificationFull],
          historicalMix: [conf.historicalMix],
          targetedMix: [conf.targetedMix ?? 0],
          originalHours: [conf.originalHours],
          manualHours: [conf.manualHours],
          editingHours: conf.hasManualHours ? [conf.manualHours ?? 0] : [conf.originalHours ?? 0],
          hasManualHours: [conf.hasManualHours],
          comment: [conf.comment],
          totalPercentage: [null],
          updated: [false],
          added: [false]
        }));
      });
      // TOTAL Lines
      if (truckTire.tiresConf.length > 0) {
        this.tireSettings.push(this.formBuilder.group({
          type: [LINE_TYPE_TOTAL],
          expanded: [expandedForTruck],
          hasChildren: [null],
          seat: [null],
          tireLifeId: [null],
          truckSpecLabel: [null],
          truckSpecId: [truckTire.truckSpecId],
          tireSpecId: [null],
          tireSpecificationFull: [null],
          historicalMix: [null],
          targetedMix: [0],
          originalHours: [null],
          manualHours: [null],
          editingHours: [0],
          hasManualHours: [null],
          comment: [null],
          totalPercentage: [0],
          updated: [null],
          added: [false]
        }));

        //Updating totalPercentage according to tire details
        this.updateTotalPercentageForTruckSpec(truckTire.truckSpecId)
      }
    });

    this.filteredTireSetting = this.tireSettings.controls as FormGroup[];
  }

  /**
   * Update the total targeted mix for a given truck specification
   * @param truckSpecId
   */
  updateTotalPercentageForTruckSpec(truckSpecId: number) {
    this.calcTotalPercentageForTruckSpec(truckSpecId);
    this.isErrorTooltipSaveBtn.set(false);
    //Check if invalid percent exist on whole truckTireList
    this.truckTiresList.forEach(truckTire => {
      let hasTireSpec: boolean = this.tireSettings.controls
        .filter((group: FormGroup<TireLineForm>) => group.controls.type.value === LINE_TYPE_TIRE_DETAIL && group.controls.truckSpecId.value === truckTire.truckSpecId)
        .length > 0;
      if (hasTireSpec && this.getTotalPercentageForTruckSpec(truckTire.truckSpecId) !== 100) {
        this.isErrorTooltipSaveBtn.set(true);
      }
    })
  }

  /**
   * Update the total targeted mix for a given truck specification
   * @param truckSpecId
   */
  calcTotalPercentageForTruckSpec(truckSpecId: number) {
    const totalPercentage = this.getTotalPercentageForTruckSpec(truckSpecId);
    const total = Number(totalPercentage) ? totalPercentage : 0;
    const totalGroup = this.tireSettings.controls.find(
      (group: FormGroup<TireLineForm>) => group.controls.type.value === LINE_TYPE_TOTAL && group.controls.truckSpecId.value === truckSpecId
    );
    if (totalGroup) {
      totalGroup.controls.totalPercentage.setValue(total);
    }
  }

  /**
   * Calculate the total targeted mix for a given truck specification by summing the targeted mix of all its tire details
   * @param truckSpecId
   */
  getTotalPercentageForTruckSpec(truckSpecId: number): number {
    return this.tireSettings.controls
      .filter((group: FormGroup<TireLineForm>) =>
        group.controls.type.value === LINE_TYPE_TIRE_DETAIL && group.controls.truckSpecId.value === truckSpecId
      )
      .map((group: FormGroup<TireLineForm>) => {
        const targetedMix = group.controls.targetedMix?.value;

        if (String(targetedMix) === "" || Number.isNaN(targetedMix)) {
          return 0;
        }

        if (targetedMix > 100) {
          group.controls.targetedMix.setValue(100);
          return 100;
        }
        return targetedMix;
      })
      .reduce((acc: number, val: number) => Number(acc) + Number(val), 0);
  }



  /**
   * Remove a tire specification from the form array and add it to the list of deleted truck tires
   * @param truckSpecId
   * @param tireSpecId
   * @protected
   */
  protected removeTireSpecification(truckSpecId: number, tireSpecId: number) {
    const index = this.tireSettings.controls.findIndex(
      (group: FormGroup<TireLineForm>) =>
        group.controls.type.value === LINE_TYPE_TIRE_DETAIL &&
        group.controls.truckSpecId.value === truckSpecId &&
        group.controls.tireSpecId.value === tireSpecId
    );
    if (index !== -1) {
      this.isModificationPending.update(() => true)
      this.tireSettings.removeAt(index);
      this.deletedTruckTires.push({truckSpecId, tireSpecId});

      //Check if there is any more tire detail for the truck specification
      const hasChildren = this.tireSettings.controls.some(
        (group: FormGroup<TireLineForm>) =>
          group.controls.type.value === LINE_TYPE_TIRE_DETAIL &&
          group.controls.truckSpecId.value === truckSpecId
      );

      if (!hasChildren) {
        //Update hasChildren for the truck line if no more tire details for the truck specification
        const truckLine = this.tireSettings.controls.find(
          (group: FormGroup<TireLineForm>) =>
            group.controls.type.value === LINE_TYPE_TRUCK &&
            group.controls.truckSpecId.value === truckSpecId
        ) as FormGroup<TireLineForm> | undefined;
        truckLine.controls.hasChildren.setValue(hasChildren);

        //Remove line total if no more tire details for the truck specification
        const totalLineIdx = this.tireSettings.controls.findIndex(
          (group: FormGroup<TireLineForm>) =>
            group.controls.type.value === LINE_TYPE_TOTAL &&
            group.controls.truckSpecId.value === truckSpecId
        );
        if (totalLineIdx !== -1) {
          this.tireSettings.removeAt(totalLineIdx);
        }
      }
      else {
        this.updateTotalPercentageForTruckSpec(truckSpecId);
      }
      //Updating the filtered list to update the view
      this.filteredTireSetting = this.tireSettings.controls as FormGroup[];
    }
  }

  /**
   * Ensure that a TOTAL line exists for a given truck specification, if not it creates one right after the last TIRE_DETAIL line of the truck specification
   * @param truckSpecId
   * @private
   */
  private ensureTotalLineForTruck(truckSpecId: number): void {
    const hasTotal = this.tireSettings.controls.some(
      (group: FormGroup<TireLineForm>) =>
        group.controls.type.value === this.LINE_TYPE_TOTAL &&
        group.controls.truckSpecId.value === truckSpecId
    );
    if (hasTotal) return;

    const truckIndex = this.tireSettings.controls.findIndex(
      (group: FormGroup<TireLineForm>) =>
        group.controls.type.value === this.LINE_TYPE_TRUCK &&
        group.controls.truckSpecId.value === truckSpecId
    );

    //Get expended for truck to apply on total line
    const truckExpanded = (this.tireSettings.at(truckIndex) as FormGroup<TireLineForm>)
      ?.controls.expanded.getRawValue() === true;

    let insertTotalIndex = truckIndex + 1;
    while (
      insertTotalIndex < this.tireSettings.length &&
      (this.tireSettings.at(insertTotalIndex) as FormGroup<TireLineForm>).controls.type.value === this.LINE_TYPE_TIRE_DETAIL &&
      (this.tireSettings.at(insertTotalIndex) as FormGroup<TireLineForm>).controls.truckSpecId.value === truckSpecId
      ) {
      insertTotalIndex++;
    }

    this.tireSettings.insert(insertTotalIndex, this.formBuilder.group({
      type: [this.LINE_TYPE_TOTAL],
      expanded: [truckExpanded],
      hasChildren: [true],
      seat: [null],
      tireLifeId: [null],
      truckSpecLabel: [null],
      truckSpecId: [truckSpecId],
      tireSpecId: [0],
      tireSpecificationFull: [null],
      historicalMix: [0],
      targetedMix: [0],
      originalHours: [null],
      manualHours: [0],
      editingHours: [0],
      hasManualHours: [false],
      comment: [''],
      totalPercentage: [0],
      updated: [false],
      added: [false]
    }));
  }

  /**
   * Open the modal to add tire specifications for a given truck specification,
   * then insert the new tire specifications in the form array right after the last TIRE_DETAIL line of the truck specification
   * @param truckSpecId
   */
  openAddTireSpecificationModal(truckSpecId: number): void {
    const truckIndex = this.tireSettings.controls.findIndex(
      (group: FormGroup<TireLineForm>) =>
        group.controls.type.value === this.LINE_TYPE_TRUCK &&
        group.controls.truckSpecId.value === truckSpecId
    );

    const totalIndex = this.tireSettings.controls.findIndex(
      (group: FormGroup<TireLineForm>) =>
        group.controls.type.value === this.LINE_TYPE_TOTAL &&
        group.controls.truckSpecId.value === truckSpecId
    );

    let insertIndex = truckIndex !== -1 ? truckIndex + 1 : this.tireSettings.length;
    if (totalIndex !== -1 && totalIndex < insertIndex) {
      insertIndex = totalIndex;
    } else if (totalIndex !== -1) {
      insertIndex = totalIndex;
    }

    const existingIds = this.getExistingTireSpecsForTruck(truckSpecId);
    const ref = this.dialog.open(AddTireSpecificationModalComponent, {
      data: {truckSpecId, tireSpecIds: existingIds}
    });

    ref.afterClosed().subscribe((result?: { truckSpecId: number; specs: TireSpecResponse[] }) => {
      if (!result || !result.specs?.length) {
        return;
      }

      this.isModificationPending.update(() => true);
      let addedCount = 0;

      for (const spec of result.specs) {
        if (this.isTireSpecAlreadyAdded(result.truckSpecId, spec.id)) continue;

        this.tireSettings.insert(insertIndex, this.formBuilder.group({
          type: [LINE_TYPE_TIRE_DETAIL],
          expanded: [true],
          hasChildren: [true],
          seat: [null],
          tireLifeId: [null],
          truckSpecLabel: [null],
          truckSpecId: [result.truckSpecId],
          tireSpecId: [spec.id],
          tireSpecificationFull: [spec.specificationFull],
          historicalMix: [0],
          targetedMix: [0],
          originalHours: [null],
          editingHours: [0],
          manualHours: [0],
          hasManualHours: [false],
          comment: [''],
          totalPercentage: [null],
          updated: [false],
          added: [true]
        }));
        insertIndex++;
        addedCount++;
      }

      if (addedCount > 0) {
        const truckLine = this.tireSettings.controls.find(
          (group: FormGroup<TireLineForm>) =>
            group.controls.type.value === this.LINE_TYPE_TRUCK &&
            group.controls.truckSpecId.value === result.truckSpecId
        ) as FormGroup<TireLineForm> | undefined;

        truckLine?.controls.hasChildren.setValue(true);
        this.ensureTotalLineForTruck(result.truckSpecId);

        this.tireSettings.controls.forEach((control: FormGroup<TireLineForm>) => {
          if (control.controls.truckSpecId.value === result.truckSpecId) {
            control.controls.expanded.setValue(true);
          }
        });

        this.savedExpandedStates.set(result.truckSpecId, true);

        this.updateTotalPercentageForTruckSpec(result.truckSpecId);
        this.applyFilters();
      }
    });
  }

  /**
   * Get the list of existing tire specification IDs for a given truck specification from the form array and the current session additions
   * @param truckSpecId
   * @private
   */
  private getExistingTireSpecsForTruck(truckSpecId: number): number[] {
    const truck = this.truckTiresList.find(t => t.truckSpecId === truckSpecId);
    return truck ? truck.tiresConf.map(conf => conf.tireSpecId) : [];
  }

  /**
   * Mark the form group as updated and recalculate the total percentage for the truck specification if needed
   * @param row
   * @param calcPercentage
   * @protected
   */
  protected tireDetailInput(row: FormGroup<TireLineForm>, calcPercentage: boolean): void {
    this.isModificationPending.set(true);
    row.controls.updated.setValue(true);

    let value = row.controls.targetedMix?.value;

    //If value is null, we dont force the number to 0 in Input for better UX
    if (String(value) !== "") {
      if (Number.isNaN(value)) {
        value = 0;
      }

      if (value > 100) {
        value = 100;
      }

      if (row.controls.targetedMix?.value !== value) {
        row.controls.targetedMix.setValue(value, { emitEvent: false });
      }
    }

    if (calcPercentage) {
      this.updateTotalPercentageForTruckSpec(row.controls.truckSpecId.value);
    }
  }

  protected targetedMixChange(row: FormGroup<TireLineForm>): void {
    const raw = row.controls.targetedMix?.value;

    if (String(raw ?? '').trim() === '') {
      row.controls.targetedMix.setValue(0, { emitEvent: true });
      this.updateTotalPercentageForTruckSpec(row.controls.truckSpecId.value);
    }
  }

  protected editingHoursChange(row: FormGroup<TireLineForm>): void {
    const raw = row.controls.editingHours?.value;

    if (String(raw ?? '').trim() === '') {
      row.controls.editingHours.setValue(0, { emitEvent: true });
      row.controls.manualHours.setValue(0);
      row.controls.hasManualHours.setValue(true);
      this.tireDetailInput(row, false);
    }
  }

  /**
   * Check if a tire specification is already added for a given truck specification in the form array to prevent duplicates when adding new tire specifications
   * @param truckSpecId
   * @param tireSpecId
   * @private
   */
  private isTireSpecAlreadyAdded(truckSpecId: number, tireSpecId: number): boolean {
    return this.tireSettings.controls.some((group: FormGroup<TireLineForm>) =>
      group.controls.type.value === this.LINE_TYPE_TIRE_DETAIL &&
      group.controls.truckSpecId.value === truckSpecId &&
      group.controls.tireSpecId.value === tireSpecId
    );
  }


  /**
   * Remove the manual hours and comment for a given tire detail line and mark it as updated
   * @param row
   * @protected
   */
  protected removeManualValue(row: FormGroup<TireLineForm>): void {
    if (row.controls.manualHours?.value != null || (row.controls.comment?.value != null && row.controls.comment?.value.length > 0)) {
      const originalHours = row.controls.originalHours.value ?? 0;

      row.controls.manualHours.setValue(null);
      row.controls.editingHours.setValue(originalHours);
      row.controls.comment.setValue(null);
      row.controls.hasManualHours.setValue(false);
      this.tireDetailInput(row, false);
    }
  }

  /**
   * Set the manual hours from the editing hours and mark the line as having manual hours and updated
   * @param row
   * @protected
   */
  protected onInputTireLife(row: FormGroup<TireLineForm>) {
    this.isModificationPending.set(true);
    row.controls.updated.setValue(true);

    let valueEditingHours = row.controls.editingHours?.value;

    //If value is null, we dont force the number to 0 in Input for bette UX
    if (String(valueEditingHours) !== "" && Number.isNaN(valueEditingHours)) {
        valueEditingHours = 0;
    }

    row.controls.editingHours.setValue(valueEditingHours, { emitEvent: false });
    row.controls.manualHours.setValue(valueEditingHours);
    row.controls.hasManualHours.setValue(true);

    this.tireDetailInput(row, false);
  }

  /**
   * Order the form array by seat or truck specification label
   * @param sort
   * @protected
   */
  protected sort(sort: Sort) {
    const colName = sort.active;

    let truckTiresListSorted = [...this._truckTiresList]
    let tireLines: FormGroup<TireLineForm>[] = this.tireSettings.controls as FormGroup<TireLineForm>[];

    if (sort.active && sort.direction !== '') {
      if (colName === this.COL_SEAT) {
        truckTiresListSorted.sort((a, b) => sort.direction === 'asc' ? ((a.seat ?? 0) - (b.seat ?? 0)) : ((b.seat ?? 0) - (a.seat ?? 0)));
      } else if (colName === this.COL_TRUCK_SPEC) {
        truckTiresListSorted.sort((a, b) => sort.direction === 'asc' ? (a.truckSpecLabel.localeCompare(b.truckSpecLabel)) : (b.truckSpecLabel.localeCompare(a.truckSpecLabel)));
      }
    }

    const tireLinesSorted: FormGroup<TireLineForm>[] = [];

    //Build sorted FormArray
    truckTiresListSorted.forEach(ttl => {
      //Addingd tire truck lines
      let tireTruckFormGroup = tireLines.find(ttlFormGroup => ttlFormGroup.controls.type.value === LINE_TYPE_TRUCK && ttlFormGroup.controls.truckSpecId.value === ttl.truckSpecId);
      tireLinesSorted.push(tireTruckFormGroup);

      //Addingd details lines
      tireLines.filter(tl => tl.controls.type.value === LINE_TYPE_TIRE_DETAIL && tl.controls.truckSpecId.value === ttl.truckSpecId).forEach(tl => {
          tireLinesSorted.push(tl);
        }
      )
      //Adding total line
      let tireTotalFormGroup = tireLines.find(tl => tl.controls.type.value === LINE_TYPE_TOTAL && tl.controls.truckSpecId.value === ttl.truckSpecId);
      if (tireTotalFormGroup) {
        tireLinesSorted.push(tireTotalFormGroup);
      }
    })

    this.tireSettings.clear();
    tireLinesSorted.forEach((group) => {
      this.tireSettings.push(group);
    });

  }
}
